import { Component } from '@theme/component';

const HOUR_MS = 3600000;
const DAY_MS = 86400000;
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * Countdown to a scheduled live auction.
 *
 * The target datetime is the single source of truth and lives on the element as
 * `data-target`. Once that datetime has passed — including the broadcast itself —
 * a repeating event rolls itself forward to the next scheduled slot, so the bar is
 * never left counting down to a date that is already gone.
 *
 * All scheduling maths is done in the event's own time zone, so a visitor in another
 * zone still counts down to the advertised local start time rather than their own
 * conversion of it.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} days
 * @property {HTMLElement} hours
 * @property {HTMLElement} minutes
 * @property {HTMLElement} seconds
 * @property {HTMLElement} [label]
 *
 * @extends {Component<Refs>}
 */
export class AuctionCountdown extends Component {
  requiredRefs = ['days', 'hours', 'minutes', 'seconds'];

  /** @type {number | undefined} */
  #interval = undefined;

  /** @type {Date | null} */
  #target = null;

  /** @type {Intl.DateTimeFormat | null} */
  #zonedFormatter = null;

  connectedCallback() {
    super.connectedCallback();

    this.#target = this.#resolveTarget();
    this.#publish();
    this.#render();
    this.#start();

    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#stop();
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
  }

  /* Ticking is pointless on a hidden tab, and the value is refreshed on return. */
  #handleVisibilityChange = () => {
    if (document.hidden) {
      this.#stop();
    } else {
      this.#render();
      this.#start();
    }
  };

  #start() {
    if (this.#interval == null) this.#interval = setInterval(() => this.#render(), 1000);
  }

  #stop() {
    if (this.#interval != null) {
      clearInterval(this.#interval);
      this.#interval = undefined;
    }
  }

  get #durationMs() {
    return (Number(this.dataset.durationHours) || 0) * HOUR_MS;
  }

  get #urgentMs() {
    return (Number(this.dataset.urgentHours) || 0) * HOUR_MS;
  }

  /**
   * Reads the calendar fields of an instant as they appear in the event's time zone.
   *
   * @param {Date} date - The instant to read.
   * @returns {{year: number, month: number, day: number, hour: number, minute: number, weekday: number}}
   */
  #zonedParts(date) {
    if (!this.#zonedFormatter) {
      const options = /** @type {Intl.DateTimeFormatOptions} */ ({
        weekday: 'short',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });

      try {
        this.#zonedFormatter = new Intl.DateTimeFormat('en-US', { ...options, timeZone: this.dataset.timezone });
      } catch {
        // An unusable time zone falls back to the visitor's own, which still counts down correctly.
        this.#zonedFormatter = new Intl.DateTimeFormat('en-US', options);
      }
    }

    /** @type {Record<string, string>} */
    const parts = {};
    for (const part of this.#zonedFormatter.formatToParts(date)) parts[part.type] = part.value;

    return {
      year: Number(parts.year),
      month: Number(parts.month),
      day: Number(parts.day),
      hour: Number(parts.hour) % 24,
      minute: Number(parts.minute),
      weekday: WEEKDAYS.indexOf(parts.weekday),
    };
  }

  /**
   * Builds the exact instant for a calendar date and hour in the event's time zone by
   * correcting for whatever UTC offset that zone has on that date.
   *
   * @param {number} year - Calendar year in the event's time zone.
   * @param {number} month - Calendar month, 1-indexed.
   * @param {number} day - Calendar day of the month.
   * @param {number} hour - Hour of the day, 0-23.
   * @returns {Date}
   */
  #zonedInstant(year, month, day, hour) {
    let guess = new Date(Date.UTC(year, month - 1, day, hour, 0, 0));

    for (let attempt = 0; attempt < 3; attempt += 1) {
      const seen = this.#zonedParts(guess);
      let dayDrift = 0;
      if (seen.day !== day) dayDrift = seen.day < day ? -24 : 24;

      const driftHours = seen.hour - hour + dayDrift;
      if (driftHours === 0 && seen.minute === 0) break;

      guess = new Date(guess.getTime() - driftHours * HOUR_MS);
    }

    return guess;
  }

  /**
   * The weekly slots this event runs. An event can run more than one show a week —
   * the Live Cast is Thursday evenings and Saturday afternoons — so the countdown
   * resolves whichever comes next instead of one being hardcoded against the other.
   *
   * @returns {{weekday: number, hour: number}[]}
   */
  get #slots() {
    /** @type {{weekday: number, hour: number}[]} */
    const slots = [];
    const configured = [
      [this.dataset.weekday, this.dataset.hour],
      [this.dataset.weekday2, this.dataset.hour2],
    ];

    for (const [rawWeekday, rawHour] of configured) {
      if (!rawWeekday || !rawHour) continue;

      const weekday = Number(rawWeekday);
      const hour = Number(rawHour);
      if (Number.isNaN(weekday) || Number.isNaN(hour)) continue;

      slots.push({ weekday, hour });
    }

    return slots;
  }

  /**
   * The next occurrence of one weekly slot, read in the event's own time zone.
   *
   * @param {number} weekday - Day of the week, 0 = Sunday.
   * @param {number} hour - Hour of the day, 0-23.
   * @returns {Date}
   */
  #nextForSlot(weekday, hour) {
    const now = this.#zonedParts(new Date());
    let ahead = (weekday - now.weekday + 7) % 7;

    // Today's broadcast is already over — go to next week's.
    if (ahead === 0 && now.hour >= hour + this.#durationMs / HOUR_MS) ahead = 7;

    const slot = this.#zonedInstant(now.year, now.month, now.day, hour);
    return new Date(slot.getTime() + ahead * DAY_MS);
  }

  /**
   * Keeps the configured datetime while it is still upcoming or still on air, and
   * otherwise falls back to the soonest of the weekly slots.
   *
   * @returns {Date | null}
   */
  #resolveTarget() {
    const configured = this.dataset.target ? new Date(this.dataset.target) : null;
    const isValid = configured != null && !Number.isNaN(configured.getTime());

    if (isValid && configured.getTime() + this.#durationMs > Date.now()) return configured;
    if (this.dataset.repeats !== 'true') return isValid ? configured : null;

    /** @type {Date | null} */
    let soonest = null;
    for (const { weekday, hour } of this.#slots) {
      const when = this.#nextForSlot(weekday, hour);
      if (!soonest || when.getTime() < soonest.getTime()) soonest = when;
    }

    return soonest ?? (isValid ? configured : null);
  }

  /* Republishes the resolved datetime so the DOM stays the single source of truth. */
  #publish() {
    if (!this.#target) return;

    this.dataset.target = this.#target.toISOString();
    this.#publishDate(this.#target);
  }

  /**
   * Writes the resolved date into every element carrying the configured class, so a date
   * line elsewhere on the page names the occurrence this countdown is actually counting to
   * and cannot go stale when the event rolls forward.
   *
   * @param {Date} date - The resolved event datetime.
   */
  #publishDate(date) {
    const targetClass = this.dataset.dateTarget;
    if (!targetClass) return;

    const formatted = this.#formatEventDate(date);
    if (!formatted) return;

    for (const host of document.getElementsByClassName(targetClass)) {
      // Horizon's text block renders <div class="text-block"><p>…</p></div>; write the leaf.
      const node = host.querySelector('.text-block > *') ?? host;
      if (node.textContent !== formatted) node.textContent = formatted;
    }
  }

  /**
   * "Thu, Sep 3 · 7:00 PM ET" — the mockup's wording, read in the event's own time zone.
   *
   * @param {Date} date - The resolved event datetime.
   * @returns {string}
   */
  #formatEventDate(date) {
    const locale = document.documentElement.lang || 'en-US';
    const options = /** @type {Intl.DateTimeFormatOptions} */ ({
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: this.dataset.timezone,
    });

    /** @type {Intl.DateTimeFormatPart[]} */
    let parts;
    try {
      // shortGeneric gives the mockup's season-neutral "ET" rather than "EDT"/"EST".
      parts = new Intl.DateTimeFormat(locale, { ...options, timeZoneName: 'shortGeneric' }).formatToParts(date);
    } catch {
      try {
        parts = new Intl.DateTimeFormat(locale, { ...options, timeZoneName: 'short' }).formatToParts(date);
      } catch {
        return '';
      }
    }

    // The mockup swaps the separator before the time for a middot; done by position so the
    // date and time can sit in whichever order the locale puts them.
    const hourIndex = parts.findIndex((part) => part.type === 'hour');
    return parts.map((part, index) => (part.type === 'literal' && index === hourIndex - 1 ? ' · ' : part.value)).join('');
  }

  /**
   * The seconds figure differs on every tick, so this writes once a second for as long as
   * the page stays open. `textContent =` is "replace all": it removes the text node and
   * inserts a new one, and that childList mutation wakes the ref observer in component.js
   * along with two Shopify platform observers, every second, for nothing. Writing the
   * existing node's value in place is a characterData mutation, which none of the three
   * subscribe to. Measured on the preview: 36 observer wakeups per 12s down to zero.
   *
   * @param {'days' | 'hours' | 'minutes' | 'seconds'} unit - The unit to write.
   * @param {number} value - The value to write, zero-padded to two digits.
   */
  #setUnit(unit, value) {
    const node = this.refs[unit];
    const text = value < 10 ? `0${value}` : String(value);
    if (!node || node.textContent === text) return;

    // The block renders each figure as a lone text node; the fallback keeps this correct
    // if that ever stops being true.
    const first = node.firstChild;
    if (first && first.nodeType === Node.TEXT_NODE && !first.nextSibling) {
      first.nodeValue = text;
    } else {
      node.textContent = text;
    }
  }

  /**
   * Unconditional classList.add/remove re-serialize the class attribute even when the
   * token set is unchanged, which reads as a DOM mutation on every tick.
   *
   * @param {string} name - The state class to set.
   * @param {boolean} on - Whether the class should be present.
   */
  #setState(name, on) {
    if (this.classList.contains(name) !== on) this.classList.toggle(name, on);
  }

  /**
   * @param {string | undefined} text - The label to write, if it has changed.
   */
  #setLabel(text) {
    const { label } = this.refs;
    if (label && text && label.textContent !== text) label.textContent = text;
  }

  #render() {
    if (!this.#target) return;

    const diff = this.#target.getTime() - Date.now();

    // The broadcast has started: the bar flips to its live state and holds at zero.
    if (diff <= 0) {
      const isOver = diff <= -this.#durationMs;
      const repeats = this.dataset.repeats === 'true';

      this.#setUnit('days', 0);
      this.#setUnit('hours', 0);
      this.#setUnit('minutes', 0);
      this.#setUnit('seconds', 0);
      this.#setState('auction-countdown--urgent', false);

      if (isOver && repeats) {
        // Move on to the next slot rather than sitting on a date that has gone.
        this.#target = this.#resolveTarget();
        this.#publish();
        return;
      }

      if (isOver) {
        // A one-off event that has finished: stop claiming to be on air, and stop
        // ticking — nothing changes again until the merchant sets a new date.
        this.#setState('auction-countdown--live', false);
        this.#setLabel(this.dataset.labelDefault);
        this.#stop();
        return;
      }

      this.#setState('auction-countdown--live', true);
      this.#setLabel(this.dataset.labelLive);
      return;
    }

    const seconds = Math.floor(diff / 1000);
    this.#setUnit('days', Math.floor(seconds / 86400));
    this.#setUnit('hours', Math.floor((seconds % 86400) / 3600));
    this.#setUnit('minutes', Math.floor((seconds % 3600) / 60));
    this.#setUnit('seconds', seconds % 60);

    const isUrgent = this.#urgentMs > 0 && diff <= this.#urgentMs;
    this.#setState('auction-countdown--live', false);
    this.#setState('auction-countdown--urgent', isUrgent);
    this.#setLabel(isUrgent ? this.dataset.labelUrgent : this.dataset.labelDefault);
  }
}

if (!customElements.get('auction-countdown')) {
  customElements.define('auction-countdown', AuctionCountdown);
}
